import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button';

/**
 * GiftDiscovery Component
 *
 * Shows the gift discovery flow after adding a person.
 * Displays status, gift recommendations, and allows user to approve.
 */

// Status steps for the gifting workflow
const WORKFLOW_STEPS = [
  { id: 'received', label: 'Request Received', icon: '📥' },
  { id: 'curating', label: 'Curating Gifts', icon: '🔍' },
  { id: 'ready', label: 'Recommendations Ready', icon: '🎁' },
  { id: 'approved', label: 'Gift Approved', icon: '✅' },
  { id: 'purchased', label: 'Purchased', icon: '🛒' },
  { id: 'shipped', label: 'On the Way', icon: '📦' },
  { id: 'delivered', label: 'Delivered', icon: '🎉' },
];

/**
 * WorkflowStatus - Shows current status in the gifting journey
 */
export function WorkflowStatus({ currentStep = 'received', className = '' }) {
  const currentIndex = WORKFLOW_STEPS.findIndex(s => s.id === currentStep);

  return (
    <div className={`bg-white rounded-2xl p-5 shadow-sm border border-slate-100 ${className}`}>
      <h3 className="font-semibold text-slate-800 mb-4">Gift Status</h3>

      <div className="relative">
        {/* Progress line */}
        <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-slate-200" />
        <div
          className="absolute left-5 top-0 w-0.5 bg-gradient-to-b from-coral-500 to-emerald-500 transition-all duration-500"
          style={{ height: `${(currentIndex / (WORKFLOW_STEPS.length - 1)) * 100}%` }}
        />

        {/* Steps */}
        <div className="space-y-4">
          {WORKFLOW_STEPS.map((step, index) => {
            const isComplete = index < currentIndex;
            const isCurrent = index === currentIndex;
            const isPending = index > currentIndex;

            return (
              <div key={step.id} className="flex items-center gap-4 relative">
                {/* Icon */}
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center text-lg z-10
                  transition-all duration-300
                  ${isComplete ? 'bg-emerald-100' : ''}
                  ${isCurrent ? 'bg-coral-100 ring-4 ring-coral-100 animate-pulse-subtle' : ''}
                  ${isPending ? 'bg-slate-100' : ''}
                `}>
                  {isComplete ? '✓' : step.icon}
                </div>

                {/* Label */}
                <div className="flex-1">
                  <p className={`
                    font-medium transition-colors
                    ${isComplete ? 'text-emerald-600' : ''}
                    ${isCurrent ? 'text-coral-600' : ''}
                    ${isPending ? 'text-slate-400' : ''}
                  `}>
                    {step.label}
                  </p>
                  {isCurrent && (
                    <p className="text-sm text-slate-500 mt-0.5">In progress...</p>
                  )}
                </div>

                {/* Status badge */}
                {isCurrent && (
                  <span className="px-2 py-1 bg-coral-100 text-coral-600 text-xs font-medium rounded-full">
                    Current
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * GiftRecommendationCard - Single gift recommendation
 */
export function GiftRecommendationCard({
  gift,
  onApprove,
  onReject,
  isSelected = false,
  className = '',
}) {
  return (
    <div className={`
      bg-white rounded-2xl overflow-hidden border-2 transition-all duration-300
      ${isSelected
        ? 'border-coral-400 shadow-lg shadow-coral-500/20'
        : 'border-slate-100 shadow-sm hover:border-slate-200 hover:shadow-md'
      }
      ${className}
    `}>
      {/* Gift Image */}
      <div className="aspect-square bg-gradient-to-br from-slate-100 to-slate-50 relative">
        {gift.image ? (
          <img
            src={gift.image}
            alt={gift.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-6xl">🎁</span>
          </div>
        )}

        {/* Price badge */}
        <div className="absolute top-3 right-3 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full shadow-sm">
          <span className="font-semibold text-slate-800">${gift.price}</span>
        </div>

        {/* Selected indicator */}
        {isSelected && (
          <div className="absolute top-3 left-3 w-8 h-8 bg-coral-500 rounded-full flex items-center justify-center shadow-lg">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}
      </div>

      {/* Gift Info */}
      <div className="p-4">
        <h4 className="font-semibold text-slate-800 mb-1">{gift.name}</h4>
        <p className="text-sm text-slate-500 mb-3 line-clamp-2">{gift.description}</p>

        {/* Why we chose this */}
        {gift.reason && (
          <div className="bg-amber-50 rounded-xl p-3 mb-3">
            <p className="text-xs text-amber-700">
              <span className="font-medium">Why this gift:</span> {gift.reason}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            variant={isSelected ? 'primary' : 'secondary'}
            onClick={() => onApprove?.(gift)}
            className="flex-1"
          >
            {isSelected ? 'Selected ✓' : 'Choose This'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * GiftDiscovery - Main discovery flow component
 */
export function GiftDiscovery({
  person,
  status = 'curating', // Current workflow status
  recommendations = [],
  onSelectGift,
  onClose,
  className = '',
}) {
  const [selectedGift, setSelectedGift] = useState(null);
  const [isLoading, setIsLoading] = useState(status === 'curating');

  // Simulate loading for demo
  useEffect(() => {
    if (status === 'curating') {
      const timer = setTimeout(() => setIsLoading(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  const occasionDisplay = person?.occasion
    ? person.occasion.charAt(0).toUpperCase() + person.occasion.slice(1)
    : 'Special Day';

  const handleSelectGift = (gift) => {
    setSelectedGift(gift);
    onSelectGift?.(gift);
  };

  return (
    <div className={`min-h-full bg-slate-50 ${className}`}>
      {/* Header */}
      <div className="bg-gradient-to-br from-coral-500 to-coral-600 text-white p-6 pb-12">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <span className="text-sm opacity-80">Gift Discovery</span>
          <div className="w-10" /> {/* Spacer */}
        </div>

        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-3xl mx-auto mb-3">
            🎁
          </div>
          <h1 className="text-2xl font-bold mb-1">
            {person?.recipientName}'s {occasionDisplay}
          </h1>
          <p className="opacity-80">
            {person?.date ? new Date(person.date).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric'
            }) : 'Upcoming'}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 -mt-6 pb-6 space-y-4">
        {/* Status Card */}
        <WorkflowStatus currentStep={status} />

        {/* Loading State */}
        {isLoading && (
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 text-center">
            <div className="w-16 h-16 mx-auto mb-4 relative">
              <div className="absolute inset-0 rounded-full border-4 border-slate-200" />
              <div className="absolute inset-0 rounded-full border-4 border-coral-500 border-t-transparent animate-spin" />
              <span className="absolute inset-0 flex items-center justify-center text-2xl">🔍</span>
            </div>
            <h3 className="font-semibold text-slate-800 mb-2">Finding Perfect Gifts</h3>
            <p className="text-slate-500 text-sm">
              We're curating thoughtful options based on {person?.recipientName}'s interests...
            </p>
          </div>
        )}

        {/* Recommendations */}
        {!isLoading && recommendations.length > 0 && (
          <div>
            <h3 className="font-semibold text-slate-800 mb-3 px-1">
              Our Recommendations
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {recommendations.map((gift) => (
                <GiftRecommendationCard
                  key={gift.id}
                  gift={gift}
                  isSelected={selectedGift?.id === gift.id}
                  onApprove={handleSelectGift}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && recommendations.length === 0 && status === 'ready' && (
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 text-center">
            <div className="text-5xl mb-4">🎯</div>
            <h3 className="font-semibold text-slate-800 mb-2">No Recommendations Yet</h3>
            <p className="text-slate-500 text-sm">
              Our team is working on finding the perfect gifts for {person?.recipientName}.
            </p>
          </div>
        )}

        {/* Selected Gift CTA */}
        {selectedGift && (
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-slate-200 shadow-lg">
            <Button
              variant="primary"
              className="w-full py-4 text-lg"
              onClick={() => {
                // Confirm selection
                onSelectGift?.(selectedGift);
                onClose?.();
              }}
            >
              <span className="flex items-center justify-center gap-2">
                <span>Approve & Send Gift</span>
                <span className="opacity-80">${selectedGift.price}</span>
              </span>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * GiftDiscoverySheet - Wrapper for use in bottom sheet
 */
export function GiftDiscoverySheet({
  person,
  onClose,
  onSelectGift,
}) {
  // Demo recommendations - in real app, these would come from API
  const demoRecommendations = [
    {
      id: '1',
      name: 'Cozy Cashmere Scarf',
      description: 'Luxuriously soft 100% cashmere scarf in a beautiful neutral tone.',
      price: 89,
      image: null,
      reason: 'Perfect for someone who loves comfort and style.',
    },
    {
      id: '2',
      name: 'Gourmet Coffee Set',
      description: 'Premium single-origin coffee beans with a beautiful ceramic mug.',
      price: 65,
      image: null,
      reason: 'They mentioned loving coffee!',
    },
    {
      id: '3',
      name: 'Personalized Photo Book',
      description: 'Custom hardcover photo book with your favorite memories together.',
      price: 75,
      image: null,
      reason: 'A thoughtful, personal touch for family.',
    },
    {
      id: '4',
      name: 'Spa Day Gift Card',
      description: 'Treat them to a relaxing day at a top-rated local spa.',
      price: 120,
      image: null,
      reason: 'Everyone deserves a little pampering!',
    },
  ];

  return (
    <GiftDiscovery
      person={person}
      status="ready"
      recommendations={demoRecommendations}
      onSelectGift={onSelectGift}
      onClose={onClose}
    />
  );
}

export default GiftDiscovery;
